import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TestTube2, Plus, Edit, Trash2, Search, RefreshCw,
  AlertCircle, X, Info, FileText, ChevronDown, ChevronLeft, ChevronRight,
  Grid3x3, Table2, Download
} from 'lucide-react';
import { labTestsAPI, categoriesAPI } from '../../services/api';

// Dropdown options
const SPECIMEN_TYPES = [
  'Blood', 'Breath', 'CFDNA Tube', 'CSF', 'EDTA-Blood', 'EDTA-Whole Blood',
  'Plasma', 'Plasma - Citrated', 'Saliva', 'Semen', 'Serum',
  'Sputum', 'Stool', 'Swab', 'Urine'
];

const DEPARTMENTS = [
  'Blood Bank/Transfusion Medicine', 'Clinical Chemistry', 'Cytology',
  'Hematology', 'Histology/Pathology', 'Hormones', 'Immunology/Serology',
  'Microbiology', 'Molecular Biology', 'Nuclear Medicine', 'Parasitology', 'Serology'
];

const TUBE_TYPES = [
  'Black Top', 'Breath Bags', 'CF Tube', 'Green Top', 'Lavender Top',
  'Purple Tube', 'Red/Yellow Top', 'Sodium citrate Light blue',
  'Sterile Container', 'Stool Container', 'Urine Container'
];

const TIME_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' }
];

const RESULT_TYPES = [
  { value: 'numeric', label: 'Numeric' },
  { value: 'text', label: 'Text' },
  { value: 'single_select', label: 'Single Select' },
  { value: 'multi_select', label: 'Multi Select' },
  { value: 'culture_grid', label: 'Culture Grid (Organism + Antibiotics)' },
  { value: 'rich_text_with_images', label: 'Rich Text + Images' }
];

const AllTests = () => {
  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cultureOrganisms, setCultureOrganisms] = useState([]);
  const [cultureAntibiotics, setCultureAntibiotics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const ITEMS_PER_PAGE = 20;

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  // Import from templates
  const [showImportModal, setShowImportModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateTests, setTemplateTests] = useState([]);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    aliases: '',
    specimen_type: '',
    department: '',
    tube_type: '',
    category_id: '',
    price: '',
    tat_value: 24,
    tat_unit: 'hours',
    tat_notes: '',
    description: '',
    why_test: '',
    high_result_meaning: '',
    low_result_meaning: '',
    video_url: '',
    is_calculated: false,
    calculation_formula: '',
    is_serial_test: false,
    serial_intervals: [],
    has_questions: false,
    questions_active: true,
    questions: [],
    print_layout_style: 'standard_vertical',
    chart_config: { chart_type: 'line', x_axis_label: '', y_axis_label: '', show_thresholds: true },
    accepts_home_visit: false,
    sections: [{ section_name: '', components: [{ name: '', result_type: 'numeric', unit: '', requires_mic: false, linked_organism_ids: [], linked_antibiotic_ids: [], has_reference_range: false, reference_ranges: [] }] }]
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [calcTestSearchQuery, setCalcTestSearchQuery] = useState('');
  const [calcTestSearchResults, setCalcTestSearchResults] = useState([]);

  // Fetch tests with FULL structure (sections, components, ranges) - no need for separate API call when editing
  const fetchTests = useCallback(async (page = currentPage) => {
    setLoading(true);
    setError('');
    try {
      // Use getAllWithFullStructure - returns tests with all nested data in ONE request
      const response = await labTestsAPI.getAllWithFullStructure({
        page,
        limit: ITEMS_PER_PAGE,
        ...(searchQuery && { search: searchQuery })
      });
      // Handle paginated response
      const data = response.data?.data;
      const items = data?.items || response.data?.data || [];
      setTests(Array.isArray(items) ? items : []);
      setTotalItems(data?.total || items.length);
      setTotalPages(Math.ceil((data?.total || items.length) / ITEMS_PER_PAGE));
    } catch (err) {
      setError('حدث خطأ في تحميل البيانات');
      setTests([]);
    }
    setLoading(false);
  }, [currentPage, searchQuery]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesAPI.getAll();
      const data = response.data?.data || response.data || [];
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setCategories([]);
    }
  }, []);

  const fetchCultureMasters = useCallback(async () => {
    try {
      const [orgRes, abRes] = await Promise.all([
        labTestsAPI.getCultureOrganisms(),
        labTestsAPI.getCultureAntibiotics()
      ]);
      setCultureOrganisms(orgRes.data?.data || []);
      setCultureAntibiotics(abRes.data?.data || []);
    } catch {
      setCultureOrganisms([]);
      setCultureAntibiotics([]);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchCultureMasters(); }, [fetchCultureMasters]);
  useEffect(() => { fetchTests(currentPage); }, [currentPage, fetchTests]);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchTests(1);
  };

  const resetForm = () => {
    setFormData({
      name_en: '', name_ar: '', aliases: '', specimen_type: '', department: '', tube_type: '',
      category_id: '', price: '', tat_value: 24, tat_unit: 'hours', tat_notes: '', description: '',
      why_test: '', high_result_meaning: '', low_result_meaning: '', video_url: '',
      is_calculated: false, calculation_formula: '', formula_tokens: [],
      is_serial_test: false, serial_intervals: [],
      has_questions: false, questions_active: true, questions: [],
      print_layout_style: 'standard_vertical',
      chart_config: { chart_type: 'line', x_axis_label: '', y_axis_label: '', show_thresholds: true },
      accepts_home_visit: false,
      sections: [{ section_name: '', components: [{ name: '', result_type: 'numeric', unit: '', options: [], requires_mic: false, linked_organism_ids: [], linked_antibiotic_ids: [], has_reference_range: false, reference_ranges: [] }] }]
    });
  };

  const handleAdd = () => { resetForm(); setShowAddModal(true); };

  // Template import functions
  const loadTemplates = async () => {
    try {
      const res = await labTestsAPI.getTemplatesList();
      setTemplates(res.data.data || res.data || []);
    } catch (err) { console.error(err); }
  };

  const loadTemplateTests = async (templateId, search = '', category = '') => {
    setTemplateLoading(true);
    try {
      const res = await labTestsAPI.getTemplateTests(templateId, { search, category });
      const data = res.data.data || res.data;
      setTemplateTests(data.tests || []);
      setTemplateCategories(data.categories || []);
    } catch (err) { console.error(err); }
    setTemplateLoading(false);
  };

  const [importedTests, setImportedTests] = useState(new Set());

  const handleImportTest = async (test) => {
    if (importedTests.has(test.name_en)) return;
    setImportLoading(test.name_en);
    try {
      await labTestsAPI.importFromTemplate({
        templateId: selectedTemplate._id,
        testNameEn: test.name_en,
        categoryNameEn: test.category_en,
      });
      setImportedTests(prev => new Set([...prev, test.name_en]));
      setError('');
      fetchTests();
      toast.success(`تم استيراد "${test.name_ar || test.name_en}" بنجاح`);
    } catch (err) {
      const msg = err.response?.data?.message || 'فشل الاستيراد';
      toast.error(msg);
    }
    setImportLoading(false);
  };

  // handleEdit now uses data already loaded (no separate API call needed!)
  const handleEdit = (test) => {
    setSelectedTest(test);
    setFormData({
      name_en: test.name_en || '',
      name_ar: test.name_ar || '',
      aliases: test.aliases || '',
      specimen_type: test.specimen_type || '',
      department: test.department || '',
      tube_type: test.tube_type || '',
      category_id: test.category_id?._id || test.category_id || '',
      price: test.price ?? '',
      tat_value: test.tat_value || 24,
      tat_unit: test.tat_unit || 'hours',
      tat_notes: test.tat_notes || '',
      description: test.description || '',
      why_test: test.why_test || '',
      high_result_meaning: test.high_result_meaning || '',
      low_result_meaning: test.low_result_meaning || '',
      video_url: test.video_url || '',
      is_calculated: test.is_calculated || false,
      calculation_formula: test.calculation_formula || '',
      formula_tokens: test.formula_tokens || [],
      is_serial_test: test.is_serial_test || false,
      serial_intervals: test.serial_intervals || [],
      has_questions: test.has_questions || false,
      questions_active: test.questions_active ?? true,
      questions: test.questions || [],
      print_layout_style: test.print_layout_style || 'standard_vertical',
      chart_config: test.chart_config || { chart_type: 'line', x_axis_label: '', y_axis_label: '', show_thresholds: true },
      accepts_home_visit: test.accepts_home_visit || false,
      sections: [{ section_name: '', components: [{ name: '', result_type: 'numeric', unit: '', options: [], requires_mic: false, linked_organism_ids: [], linked_antibiotic_ids: [], has_reference_range: false, reference_ranges: [] }] }]
    });

    // Use sections data already loaded with the test (no API call needed!)
    if (test.sections && test.sections.length > 0) {
      const loadedSections = test.sections.map(section => ({
        _id: section._id,
        section_name: section.section_name || '',
        components: section.components?.map(comp => ({
          _id: comp._id,
          name: comp.name || '',
          result_type: comp.result_type || 'numeric',
          unit: comp.unit || '',
          options: comp.options || [],
          requires_mic: comp.requires_mic || false,
          linked_organism_ids: comp.linked_organism_ids || [],
          linked_antibiotic_ids: comp.linked_antibiotic_ids || [],
          has_reference_range: comp.has_reference_range || false,
          reference_ranges: comp.referenceRanges?.map(range => ({
            _id: range._id,
            gender: range.gender || 'Any',
            min_age: range.age_from ?? 0,
            max_age: range.age_to ?? 100,
            min_value: range.low_value ?? '',
            max_value: range.high_value ?? '',
            critical_value: range.critical_value ?? '',
            critical_label: range.critical_label ?? ''
          })) || []
        })) || [{ name: '', result_type: 'numeric', unit: '', options: [], requires_mic: false, has_reference_range: false, reference_ranges: [] }]
      }));
      setFormData(prev => ({ ...prev, sections: loadedSections }));
    }

    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.name_en.trim() || !formData.specimen_type || !formData.department) {
      setError('الحقول المطلوبة: اسم التحليل، نوع العينة، القسم');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { sections, questions, ...testData } = formData;

      // Filter out empty questions
      const validQuestions = (questions || []).filter(q => q.question_text && q.question_text.trim());
      testData.questions = validQuestions;

      // Cleanup empty values that might cause BSON/validation errors
      if (!testData.category_id) delete testData.category_id;
      if (testData.price === '') delete testData.price;

      let testId;

      if (showEditModal && selectedTest) {
        await labTestsAPI.update(selectedTest._id, testData);
        testId = selectedTest._id;

        // Delete existing sections first (to replace with new ones)
        try {
          const existingSections = await labTestsAPI.getSections(testId);
          const sectionsData = existingSections.data?.data || [];
          for (const section of sectionsData) {
            await labTestsAPI.deleteSection(section._id);
          }
        } catch (e) {
          // Ignore if no sections exist
        }
      } else {
        const response = await labTestsAPI.create(testData);
        testId = response.data?.data?._id;
      }

      // Save sections if testId exists
      if (testId && sections && sections.length > 0) {
        for (const section of sections) {
          if (section.section_name || section.components.some(c => c.name)) {
            // Create section
            const sectionResponse = await labTestsAPI.createSection(testId, {
              section_name: section.section_name || 'Default Section',
              sort_order: 0
            });
            const sectionId = sectionResponse.data?.data?._id;

            // Create components for this section
            if (sectionId && section.components) {
              for (const comp of section.components) {
                if (comp.name) {
                  const compResponse = await labTestsAPI.createComponent(sectionId, {
                    name: comp.name,
                    result_type: comp.result_type || 'numeric',
                    unit: comp.unit || '',
                    options: comp.options || [],
                    requires_mic: comp.requires_mic || false,
                    linked_organism_ids: comp.linked_organism_ids || [],
                    linked_antibiotic_ids: comp.linked_antibiotic_ids || [],
                    has_reference_range: comp.has_reference_range || false
                  });
                  const componentId = compResponse.data?.data?._id;

                  // Create reference ranges for this component
                  if (componentId && comp.has_reference_range && comp.reference_ranges) {
                    for (const range of comp.reference_ranges) {
                      await labTestsAPI.createReferenceRange(componentId, {
                        gender: range.gender || 'Any',
                        age_from: range.min_age ?? 0,
                        age_to: range.max_age ?? 100,
                        age_unit: 'years',
                        low_value: range.min_value !== '' ? parseFloat(range.min_value) : null,
                        high_value: range.max_value !== '' ? parseFloat(range.max_value) : null,
                        ...(range.critical_value !== '' && range.critical_value != null && { critical_value: parseFloat(range.critical_value) }),
                        ...(range.critical_label && { critical_label: range.critical_label })
                      });
                    }
                  }
                }
              }
            }
          }
        }
      }

      fetchTests();
      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedTest) return;
    setDeleting(true);
    try {
      await labTestsAPI.delete(selectedTest._id);
      setTests(prev => prev.filter(t => t._id !== selectedTest._id));
      setShowDeleteModal(false);
      setSelectedTest(null);
    } catch (err) {
      setError('حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, { section_name: '', components: [{ name: '', result_type: 'numeric', unit: '', requires_mic: false, linked_organism_ids: [], linked_antibiotic_ids: [], has_reference_range: false, reference_ranges: [] }] }]
    }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const addComponent = (sectionIndex) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].components.push({ name: '', result_type: 'numeric', unit: '', requires_mic: false, linked_organism_ids: [], linked_antibiotic_ids: [], has_reference_range: false, reference_ranges: [] });
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const removeComponent = (sectionIndex, componentIndex) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].components = newSections[sectionIndex].components.filter((_, i) => i !== componentIndex);
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const updateSection = (sectionIndex, field, value) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex][field] = value;
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const updateComponent = (sectionIndex, componentIndex, field, value) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].components[componentIndex][field] = value;
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const addReferenceRange = (sectionIndex, componentIndex) => {
    const newSections = [...formData.sections];
    if (!newSections[sectionIndex].components[componentIndex].reference_ranges) {
      newSections[sectionIndex].components[componentIndex].reference_ranges = [];
    }
    newSections[sectionIndex].components[componentIndex].reference_ranges.push({
      gender: 'Any',
      min_age: 0,
      max_age: 100,
      min_value: '',
      max_value: '',
      critical_value: '',
      critical_label: ''
    });
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const removeReferenceRange = (sectionIndex, componentIndex, rangeIndex) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].components[componentIndex].reference_ranges =
      newSections[sectionIndex].components[componentIndex].reference_ranges.filter((_, i) => i !== rangeIndex);
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const updateReferenceRange = (sectionIndex, componentIndex, rangeIndex, field, value) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].components[componentIndex].reference_ranges[rangeIndex][field] = value;
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const filteredTests = tests.filter(t =>
    t.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.name_ar?.includes(searchQuery) ||
    t.aliases?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const inputClasses = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
  const selectClasses = "w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700 cursor-pointer";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <TestTube2 className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إدارة التحاليل</h1>
              <p className="text-slate-500 text-sm mt-1">
                إجمالي <span className="font-semibold text-slate-700">{totalItems}</span> تحليل
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'cards'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
                  }`}
                title="عرض البطاقات"
              >
                <Grid3x3 className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
                  }`}
                title="عرض الجدول"
              >
                <Table2 className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            <button
              onClick={() => { setShowImportModal(true); loadTemplates(); }}
              className="flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
            >
              <Download className="w-4 h-4" />
              استيراد من القوالب
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAdd}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              <span>إضافة تحليل</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ادخل اسم التحليل..."
                className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
              />
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <button onClick={fetchTests} className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="mr-auto"><X className="w-4 h-4" /></button>
          </motion.div>
        )}

        {/* Tests Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filteredTests.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-20 text-center">
            <TestTube2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">لا توجد تحاليل</p>
            <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">إضافة تحليل</button>
          </div>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTests.map((test, index) => (
                  <motion.div
                    key={test._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">{test.name_en}</h3>
                          {test.name_ar && <p className="text-sm text-gray-500">{test.name_ar}</p>}
                        </div>
                        {test.price > 0 && (
                          <span className="text-lg font-bold text-blue-600">{test.price} ر.س</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mb-3">ID: {test._id?.slice(-6)}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {test.category_id?.name_en && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">{test.category_id.name_en}</span>
                        )}
                        {test.department && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{test.department}</span>
                        )}
                        {test.has_questions && (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">❓ أسئلة</span>
                        )}
                        {test.accepts_home_visit && (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">🏠 زيارة منزلية</span>
                        )}
                        {test.specimen_type && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{test.specimen_type}</span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => { setSelectedTest(test); setShowDetailsModal(true); }}
                          className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
                        >
                          <Info className="w-4 h-4" />
                          تفاصيل
                        </button>
                        <button
                          onClick={() => handleEdit(test)}
                          className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
                        >
                          <Edit className="w-4 h-4" />
                          تعديل
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => { setSelectedTest(test); setShowDeleteModal(true); }}
                      className="w-full py-3 border-t border-gray-100 text-red-600 hover:bg-red-50 flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      حذف
                    </button>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden"
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم التحليل</th>
                        <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الفئة</th>
                        <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">القسم</th>
                        <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">نوع العينة</th>
                        <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">السعر</th>
                        <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTests.map((test, index) => (
                        <motion.tr
                          key={test._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.02 }}
                          className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                        >
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                                <TestTube2 className="w-4 h-4 text-slate-600" strokeWidth={2} />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 text-sm truncate">{test.name_en || '-'}</p>
                                {test.name_ar && (
                                  <p className="text-xs text-slate-500 mt-0.5 truncate">{test.name_ar}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            {test.category_id?.name_en ? (
                              <span className="px-2.5 py-1 bg-red-50 text-red-700 rounded-md text-xs font-medium border border-red-200">
                                {test.category_id.name_en}
                              </span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-5">
                            {test.department ? (
                              <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200">
                                {test.department}
                              </span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-5">
                            {test.specimen_type ? (
                              <span className="text-sm text-slate-700">{test.specimen_type}</span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-5">
                            {test.price > 0 ? (
                              <span className="font-bold text-slate-900 text-sm tabular-nums">
                                {test.price} <span className="text-slate-500 text-xs">ر.س</span>
                              </span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => { setSelectedTest(test); setShowDetailsModal(true); }}
                                className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                                title="عرض التفاصيل"
                              >
                                <Info className="w-4 h-4" strokeWidth={2} />
                              </button>
                              <button
                                onClick={() => handleEdit(test)}
                                className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                                title="تعديل"
                              >
                                <Edit className="w-4 h-4" strokeWidth={2} />
                              </button>
                              <button
                                onClick={() => { setSelectedTest(test); setShowDeleteModal(true); }}
                                className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                                title="حذف"
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={2} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm text-gray-600">
                  عرض {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} من {totalItems}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  {/* Page numbers */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {(showAddModal || showEditModal) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
              onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
            >
              <motion.div
                initial={{ scale: 0.9, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 50 }}
                className="bg-white rounded-2xl w-full max-w-4xl shadow-xl my-8"
                onClick={e => e.stopPropagation()}
              >
                <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl sticky top-0">
                  <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                    <TestTube2 className="w-5 h-5" />
                    {showEditModal ? 'تعديل التحليل' : 'إضافة تحليل جديد'}
                  </h3>
                  <button onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="text-slate-400 hover:text-slate-100 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Test Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name_en}
                        onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                        className={inputClasses}
                        placeholder="Test Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Aliases</label>
                      <input
                        type="text"
                        value={formData.aliases}
                        onChange={e => setFormData({ ...formData, aliases: e.target.value })}
                        className={inputClasses}
                        placeholder="Comma separated"
                      />
                    </div>
                  </div>

                  {/* Dropdowns Row 1 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Specimen Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.specimen_type}
                        onChange={e => setFormData({ ...formData, specimen_type: e.target.value })}
                        className={selectClasses}
                      >
                        <option value="">Select Specimen Type</option>
                        {SPECIMEN_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Department <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.department}
                          onChange={e => setFormData({ ...formData, department: e.target.value })}
                          className={selectClasses}
                        >
                          <option value="">Select Department</option>
                          {DEPARTMENTS.map(dept => (
                            <option key={dept} value={dept}>{dept}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                  {/* Dropdowns Row 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tube Type</label>
                      <select
                        value={formData.tube_type}
                        onChange={e => setFormData({ ...formData, tube_type: e.target.value })}
                        className={selectClasses}
                      >
                        <option value="">Select Tube Type</option>
                        {TUBE_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Test Category</label>
                      <select
                        value={formData.category_id}
                        onChange={e => setFormData({ ...formData, category_id: e.target.value })}
                        className={selectClasses}
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name_en}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* TAT */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Turnaround Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.tat_value}
                        onChange={e => setFormData({ ...formData, tat_value: parseInt(e.target.value) || 0 })}
                        className={inputClasses}
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time Unit <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.tat_unit}
                        onChange={e => setFormData({ ...formData, tat_unit: e.target.value })}
                        className={selectClasses}
                      >
                        {TIME_UNITS.map(unit => (
                          <option key={unit.value} value={unit.value}>{unit.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">TAT Notes</label>
                      <input
                        type="text"
                        value={formData.tat_notes}
                        onChange={e => setFormData({ ...formData, tat_notes: e.target.value })}
                        className={inputClasses}
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  {/* Text Areas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      className={`${inputClasses} text-gray-900`}
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Why Get Tested?</label>
                    <textarea
                      value={formData.why_test}
                      onChange={e => setFormData({ ...formData, why_test: e.target.value })}
                      className={`${inputClasses} text-gray-900`}
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">What Does a High Result Mean?</label>
                    <textarea
                      value={formData.high_result_meaning}
                      onChange={e => setFormData({ ...formData, high_result_meaning: e.target.value })}
                      className={`${inputClasses} text-gray-900`}
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">What Does a Low Result Mean?</label>
                    <textarea
                      value={formData.low_result_meaning}
                      onChange={e => setFormData({ ...formData, low_result_meaning: e.target.value })}
                      className={`${inputClasses} text-gray-900`}
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Video URL</label>
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={e => setFormData({ ...formData, video_url: e.target.value })}
                      className={inputClasses}
                      placeholder="https://"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_calculated}
                        onChange={e => setFormData({ ...formData, is_calculated: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">This test is calculated using an equation</span>
                    </label>
                    <p className="text-xs text-blue-500 mr-8">Enable this to create a test that calculates its result from other test values.</p>

                    {/* Equation Builder - Shows when is_calculated is true */}
                    {formData.is_calculated && (
                      <div className="border border-gray-200 rounded-xl p-4 mr-4 bg-gray-50">
                        <h4 className="font-semibold text-gray-800 mb-4 text-left">مبني المعادلات (Equation Builder)</h4>

                        {/* Step 1: Search Tests */}
                        <div className="mb-6">
                          <p className="text-sm text-blue-600 mb-2 text-left">الخطوة 1: ابحث عن تحليل لإضافته كمتغير</p>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Type to search for tests..."
                              value={calcTestSearchQuery}
                              onChange={async (e) => {
                                const q = e.target.value;
                                setCalcTestSearchQuery(q);
                                if (q.length >= 2) {
                                  try {
                                    const response = await labTestsAPI.searchByName({
                                      search: q,
                                      exclude_id: selectedTest?._id
                                    });
                                    setCalcTestSearchResults(response.data?.data || []);
                                  } catch (err) {
                                    setCalcTestSearchResults([]);
                                  }
                                } else {
                                  setCalcTestSearchResults([]);
                                }
                              }}
                              className={inputClasses}
                              dir="ltr"
                            />
                            <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                          </div>

                          {/* Search Results Dropdown */}
                          {calcTestSearchResults.length > 0 && (
                            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white mt-1 relative z-10 shadow-lg" dir="ltr">
                              {calcTestSearchResults.map(test => (
                                <button
                                  key={test._id}
                                  type="button"
                                  onClick={() => {
                                    // Add the test as a token
                                    const newTokens = [...(formData.formula_tokens || []), { type: 'variable', id: test._id, label: test.name_en }];
                                    setFormData({
                                      ...formData,
                                      formula_tokens: newTokens,
                                      calculation_formula: newTokens.map(t => t.type === 'variable' ? `[${t.id}]` : t.value).join('')
                                    });
                                    setCalcTestSearchQuery('');
                                    setCalcTestSearchResults([]);
                                  }}
                                  className="w-fulltext-left px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 flex items-center justify-between"
                                >
                                  <div>
                                    <span className="font-medium text-gray-800 text-sm">{test.name_en}</span>
                                  </div>
                                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded font-medium">+ Add</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Step 2: Formula Building */}
                        <div className="mb-6">
                          <p className="text-sm text-blue-600 mb-2 text-left">الخطوة 2: قم ببناء المعادلة الخاصة بك</p>

                          {/* Visual Token Box */}
                          <div className="min-h-[60px] p-3 bg-white border border-gray-300 rounded-xl mb-3 flex flex-wrap gap-2 items-center" dir="ltr">
                            {(!formData.formula_tokens || formData.formula_tokens.length === 0) ? (
                              <span className="text-gray-400 text-sm italic">Add tests or operators to build your formula...</span>
                            ) : (
                              formData.formula_tokens.map((token, idx) => (
                                <div key={idx} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm transition-all ${token.type === 'variable'
                                  ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                  : 'bg-white text-gray-800 border border-gray-300'
                                  }`}>
                                  <span>{token.type === 'variable' ? token.label : token.value}</span>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Operator Keyboard & Controls */}
                          <div className="flex flex-wrap items-center justify-between gap-4" dir="ltr">
                            <div className="flex flex-wrap gap-2">
                              {['+', '-', '*', '/', '(', ')'].map(op => (
                                <button
                                  key={op}
                                  type="button"
                                  onClick={() => {
                                    const newTokens = [...(formData.formula_tokens || []), { type: 'operator', value: op }];
                                    setFormData({
                                      ...formData,
                                      formula_tokens: newTokens,
                                      calculation_formula: newTokens.map(t => t.type === 'variable' ? `[${t.id}]` : t.value).join('')
                                    });
                                  }}
                                  className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-lg font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all"
                                >
                                  {op === '*' ? '×' : op}
                                </button>
                              ))}
                            </div>

                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const tokens = formData.formula_tokens || [];
                                  if (tokens.length === 0) return;
                                  const newTokens = tokens.slice(0, -1);
                                  setFormData({
                                    ...formData,
                                    formula_tokens: newTokens,
                                    calculation_formula: newTokens.map(t => t.type === 'variable' ? `[${t.id}]` : t.value).join('')
                                  });
                                }}
                                disabled={!formData.formula_tokens || formData.formula_tokens.length === 0}
                                className="px-3 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-medium shadow-sm flex items-center gap-1"
                              >
                                ⌫ مسح الأخير
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({ ...formData, formula_tokens: [], calculation_formula: '' })}
                                disabled={!formData.formula_tokens || formData.formula_tokens.length === 0}
                                className="px-3 py-2 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 text-sm font-medium shadow-sm"
                              >
                                تفريغ
                              </button>
                            </div>
                          </div>

                          <p className="text-xs text-gray-500 mt-3 text-left">Internal structure: <code className="bg-gray-100 px-1 rounded text-[10px]">{formData.calculation_formula || 'Empty'}</code></p>
                        </div>
                      </div>
                    )}

                    <label className="flex items-center gap-3 cursor-pointer mt-4">
                      <input
                        type="checkbox"
                        checked={formData.is_serial_test}
                        onChange={e => {
                          const checked = e.target.checked;
                          const updates = { is_serial_test: checked };
                          // When unchecking serial and layout was serial_with_chart, reset to standard_vertical
                          if (!checked && formData.print_layout_style === 'serial_with_chart') {
                            updates.print_layout_style = 'standard_vertical';
                          }
                          setFormData(prev => ({ ...prev, ...updates }));
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Serial Test (مثل GTT متعدد الأوقات)</span>
                    </label>
                    {formData.is_serial_test && (
                      <div className="mr-4 border border-blue-100 rounded-xl bg-blue-50 p-4">
                        <label className="block text-sm font-medium text-blue-800 mb-2">مراحل السحب الزمنية (مفصولة بفاصلة)</label>
                        <input
                          type="text"
                          value={(formData.serial_intervals || []).join(', ')}
                          onChange={(e) => {
                            const serial_intervals = e.target.value.split(',').map((x) => x.trim()).filter(Boolean);
                            setFormData({ ...formData, serial_intervals });
                          }}
                          className={inputClasses}
                          placeholder="Fasting, After 1 Hour, After 2 Hours"
                        />
                      </div>
                    )}

                    {/* Print Layout Style Dropdown */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">تخطيط التقرير (Print Layout Style)</label>
                      <select
                        value={formData.print_layout_style}
                        onChange={e => setFormData({ ...formData, print_layout_style: e.target.value })}
                        className={selectClasses}
                      >
                        <option value="standard_vertical">عمودي تقليدي</option>
                        <option value="two_column_grid">شبكة عمودين</option>
                        <option value="three_column_grid">شبكة ثلاثة أعمدة</option>
                        {formData.is_serial_test && (
                          <option value="serial_with_chart">تسلسلي مع رسم بياني</option>
                        )}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">اختر كيف تظهر النتائج في التقرير المطبوع</p>
                    </div>

                    {/* Chart Config Section - shown only when serial_with_chart is selected */}
                    {formData.print_layout_style === 'serial_with_chart' && (
                      <div className="mr-4 border border-blue-100 rounded-xl bg-blue-50 p-4 mt-4">
                        <label className="block text-sm font-semibold text-blue-800 mb-3">إعدادات الرسم البياني (Chart Settings)</label>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">نوع الرسم (Chart Type)</label>
                            <select
                              value={formData.chart_config?.chart_type || 'line'}
                              onChange={e => setFormData({ ...formData, chart_config: { ...formData.chart_config, chart_type: e.target.value } })}
                              className="w-full px-3 py-2 border border-blue-200 bg-white rounded-lg text-sm"
                            >
                              <option value="line">خطي (Line Chart)</option>
                              <option value="bar">أعمدة (Bar Chart)</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">عنوان المحور الأفقي (X Axis Label)</label>
                            <input
                              type="text"
                              value={formData.chart_config?.x_axis_label || ''}
                              onChange={e => setFormData({ ...formData, chart_config: { ...formData.chart_config, x_axis_label: e.target.value } })}
                              className="w-full px-3 py-2 border border-blue-200 bg-white rounded-lg text-sm"
                              placeholder="مثال: الوقت (دقيقة)"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">عنوان المحور العمودي (Y Axis Label)</label>
                            <input
                              type="text"
                              value={formData.chart_config?.y_axis_label || ''}
                              onChange={e => setFormData({ ...formData, chart_config: { ...formData.chart_config, y_axis_label: e.target.value } })}
                              className="w-full px-3 py-2 border border-blue-200 bg-white rounded-lg text-sm"
                              placeholder="مثال: التركيز (ppm)"
                            />
                          </div>
                          <div className="flex items-end pb-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.chart_config?.show_thresholds ?? true}
                                onChange={e => setFormData({ ...formData, chart_config: { ...formData.chart_config, show_thresholds: e.target.checked } })}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">إظهار خطوط العتبة الحرجة في الرسم</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    <label className="flex items-center gap-3 cursor-pointer mt-4">
                      <input
                        type="checkbox"
                        checked={formData.accepts_home_visit}
                        onChange={e => setFormData({ ...formData, accepts_home_visit: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">قبول الزيارات المنزلية لهذا التحليل</span>
                    </label>

                    {/* Questions Section */}
                    <label className="flex items-center gap-3 cursor-pointer mt-4">
                      <input
                        type="checkbox"
                        checked={formData.has_questions}
                        onChange={e => setFormData({ ...formData, has_questions: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">إضافة أسئلة للتحليل</span>
                    </label>

                    {formData.has_questions && (
                      <div className="border border-gray-200 rounded-xl p-4 mr-4 bg-blue-50 mt-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                              <span className="text-blue-600">❓</span> أسئلة التحليل
                            </h4>
                            <p className="text-xs text-amber-600 mt-1">
                              ⚠️ هذه الأسئلة ستظهر لموظف الاستقبال عند الحجز فقط
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.questions_active}
                                onChange={e => setFormData({ ...formData, questions_active: e.target.checked })}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">تفعيل الأسئلة</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const newQuestions = [...(formData.questions || []), {
                                  question_text: '',
                                  question_type: 'yes_no',
                                  options: ['نعم', 'لا'],
                                  is_required: false
                                }];
                                setFormData({ ...formData, questions: newQuestions });
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                            >
                              + إضافة سؤال
                            </button>
                          </div>
                        </div>

                        {(formData.questions || []).length === 0 ? (
                          <div className="text-center py-8 text-gray-500 text-sm">
                            لا توجد أسئلة. اضغط على "إضافة سؤال" لإضافة سؤال جديد.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {(formData.questions || []).map((q, qIndex) => (
                              <div key={qIndex} className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex items-start justify-between mb-3">
                                  <span className="text-sm font-semibold text-blue-600">سؤال {qIndex + 1}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newQuestions = formData.questions.filter((_, i) => i !== qIndex);
                                      setFormData({ ...formData, questions: newQuestions });
                                    }}
                                    className="text-red-500 hover:text-red-600 text-sm"
                                  >
                                    حذف
                                  </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                  <div>
                                    <label className="text-xs text-gray-500 mb-1 block">نص السؤال</label>
                                    <input
                                      type="text"
                                      value={q.question_text}
                                      onChange={e => {
                                        const newQuestions = [...formData.questions];
                                        newQuestions[qIndex].question_text = e.target.value;
                                        setFormData({ ...formData, questions: newQuestions });
                                      }}
                                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                      placeholder="مثال: هل أنت صائم؟"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500 mb-1 block">نوع السؤال</label>
                                    <select
                                      value={q.question_type}
                                      onChange={e => {
                                        const newQuestions = [...formData.questions];
                                        newQuestions[qIndex].question_type = e.target.value;
                                        if (e.target.value === 'yes_no') {
                                          newQuestions[qIndex].options = ['نعم', 'لا'];
                                        } else if (e.target.value === 'text' || e.target.value === 'number') {
                                          newQuestions[qIndex].options = [];
                                        }
                                        setFormData({ ...formData, questions: newQuestions });
                                      }}
                                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                    >
                                      <option value="yes_no">نعم / لا</option>
                                      <option value="text">نص</option>
                                      <option value="number">رقم</option>
                                      <option value="single_select">اختيار واحد</option>
                                      <option value="multi_select">اختيار متعدد</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="flex items-center gap-4 mb-3">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={q.is_required}
                                      onChange={e => {
                                        const newQuestions = [...formData.questions];
                                        newQuestions[qIndex].is_required = e.target.checked;
                                        setFormData({ ...formData, questions: newQuestions });
                                      }}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                    />
                                    <span className="text-sm text-gray-700">إلزامي</span>
                                  </label>
                                </div>

                                {/* Options for single/multi select */}
                                {(q.question_type === 'single_select' || q.question_type === 'multi_select') && (
                                  <div className="mb-3">
                                    <label className="text-xs text-gray-500 mb-1 block">الخيارات (فاصل بفاصلة)</label>
                                    <input
                                      type="text"
                                      value={(q.options || []).join(', ')}
                                      onChange={e => {
                                        const newQuestions = [...formData.questions];
                                        newQuestions[qIndex].options = e.target.value.split(',').map(o => o.trim()).filter(o => o);
                                        setFormData({ ...formData, questions: newQuestions });
                                      }}
                                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                      placeholder="خيار 1, خيار 2, خيار 3"
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Test Sections */}
                  <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-800">Test Sections</h4>
                        <button
                          type="button"
                          onClick={addSection}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                        >
                          Add Section
                        </button>
                      </div>

                      {formData.sections.map((section, sIndex) => (
                        <div key={sIndex} className="border border-gray-100 rounded-lg p-4 mb-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-4">
                            <input
                              type="text"
                              value={section.section_name}
                              onChange={e => updateSection(sIndex, 'section_name', e.target.value)}
                              className="px-3 py-2 border border-gray-200 rounded-lg flex-1 ml-4"
                              placeholder="Section Name"
                            />
                            <button
                              type="button"
                              onClick={() => removeSection(sIndex)}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                            >
                              Remove Section
                            </button>
                          </div>

                          <div className="space-y-4">
                            <h5 className="text-sm font-medium text-gray-600">Components</h5>
                            {section.components.map((comp, cIndex) => (
                              <div key={cIndex} className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="grid grid-cols-4 gap-2 items-end mb-4">
                                  <div>
                                    <label className="text-xs text-gray-500">Component Name</label>
                                    <input
                                      type="text"
                                      value={comp.name}
                                      onChange={e => updateComponent(sIndex, cIndex, 'name', e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Result Type</label>
                                    <select
                                      value={comp.result_type}
                                      onChange={e => updateComponent(sIndex, cIndex, 'result_type', e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                    >
                                      {RESULT_TYPES.map(rt => (
                                        <option key={rt.value} value={rt.value}>{rt.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Unit</label>
                                    <input
                                      type="text"
                                      value={comp.unit}
                                      onChange={e => updateComponent(sIndex, cIndex, 'unit', e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeComponent(sIndex, cIndex)}
                                    className="px-3 py-1.5 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                                  >
                                    Remove Component
                                  </button>
                                </div>

                                {/* Options for Select Types */}
                                {(comp.result_type === 'single_select' || comp.result_type === 'multi_select') && (
                                  <div className="mb-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                                    <label className="text-sm font-semibold text-blue-800 mb-3 block flex items-center gap-2">
                                      <span>خيارات النتيجة</span>
                                      <span className="text-xs font-normal text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                        {(comp.options || []).length} خيارات
                                      </span>
                                    </label>

                                    <div className="space-y-2 mb-3">
                                      {(comp.options || []).map((opt, optIndex) => (
                                        <div key={optIndex} className="flex items-center gap-2">
                                          <div className="flex-1 relative">
                                            <input
                                              type="text"
                                              value={opt}
                                              onChange={e => {
                                                const newOpts = [...(comp.options || [])];
                                                newOpts[optIndex] = e.target.value;
                                                updateComponent(sIndex, cIndex, 'options', newOpts);
                                              }}
                                              className="w-full px-3 py-2 border border-blue-200 bg-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 pr-10"
                                              placeholder={`الخيار ${optIndex + 1}`}
                                            />
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newOpts = [...(comp.options || [])];
                                              newOpts.splice(optIndex, 1);
                                              updateComponent(sIndex, cIndex, 'options', newOpts);
                                            }}
                                            className="p-2 text-red-500 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors flex-shrink-0"
                                            title="حذف الخيار"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newOpts = [...(comp.options || []), ''];
                                        updateComponent(sIndex, cIndex, 'options', newOpts);
                                      }}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-100 rounded-lg transition-colors font-medium"
                                    >
                                      <Plus className="w-4 h-4" />
                                      إضافة خيار جديد
                                    </button>
                                  </div>
                                )}

                                {comp.result_type === 'culture_grid' && (
                                  <div className="mb-4 bg-emerald-50/60 p-4 rounded-xl border border-emerald-100">
                                    <p className="text-sm font-semibold text-emerald-800 mb-3">Culture Grid Configuration</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-xs text-emerald-700 mb-1 block">Linked Organisms</label>
                                        <select
                                          multiple
                                          value={comp.linked_organism_ids || []}
                                          onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                                            updateComponent(sIndex, cIndex, 'linked_organism_ids', selected);
                                          }}
                                          className="w-full px-2 py-2 border border-emerald-200 rounded text-sm h-28 bg-white"
                                        >
                                          {cultureOrganisms.map((row) => (
                                            <option key={row._id} value={row._id}>{row.name}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-xs text-emerald-700 mb-1 block">Linked Antibiotics</label>
                                        <select
                                          multiple
                                          value={comp.linked_antibiotic_ids || []}
                                          onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                                            updateComponent(sIndex, cIndex, 'linked_antibiotic_ids', selected);
                                          }}
                                          className="w-full px-2 py-2 border border-emerald-200 rounded text-sm h-28 bg-white"
                                        >
                                          {cultureAntibiotics.map((row) => (
                                            <option key={row._id} value={row._id}>{row.name}</option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Requires MIC */}
                                <div className="mb-3">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={comp.requires_mic || false}
                                      onChange={e => updateComponent(sIndex, cIndex, 'requires_mic', e.target.checked)}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Requires MIC</span>
                                  </label>
                                </div>

                                {/* Has Reference Range */}
                                <div>
                                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                                    <input
                                      type="checkbox"
                                      checked={comp.has_reference_range || false}
                                      onChange={e => {
                                        updateComponent(sIndex, cIndex, 'has_reference_range', e.target.checked);
                                        if (e.target.checked && (!comp.reference_ranges || comp.reference_ranges.length === 0)) {
                                          addReferenceRange(sIndex, cIndex);
                                        }
                                      }}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                    />
                                    <span className="text-sm font-medium text-blue-600">Has Reference Range</span>
                                  </label>

                                  {comp.has_reference_range && (
                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                      <h6 className="text-sm font-medium text-blue-600 mb-3 text-left">Reference Ranges</h6>

                                      {(comp.reference_ranges || []).map((range, rIndex) => (
                                        <div key={rIndex} className="bg-white p-4 rounded-lg border border-gray-200 mb-3">
                                          <div className="grid grid-cols-3 gap-3 mb-3">
                                            <div>
                                              <label className="text-xs text-blue-600 font-medium">Gender</label>
                                              <select
                                                value={range.gender}
                                                onChange={e => updateReferenceRange(sIndex, cIndex, rIndex, 'gender', e.target.value)}
                                                className="w-full px-2 py-1.5 bg-gray-100 border border-gray-200 rounded text-sm"
                                              >
                                                <option value="Any">Both</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                              </select>
                                            </div>
                                            <div>
                                              <label className="text-xs text-gray-500">Min Age</label>
                                              <input
                                                type="number"
                                                value={range.min_age}
                                                onChange={e => updateReferenceRange(sIndex, cIndex, rIndex, 'min_age', parseInt(e.target.value) || 0)}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                min="0"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-gray-500">Max Age</label>
                                              <input
                                                type="number"
                                                value={range.max_age}
                                                onChange={e => updateReferenceRange(sIndex, cIndex, rIndex, 'max_age', parseInt(e.target.value) || 100)}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                min="0"
                                              />
                                            </div>
                                          </div>
                                          <div className="grid grid-cols-2 gap-3 mb-3">
                                            <div>
                                              <label className="text-xs text-gray-500">Min Value</label>
                                              <input
                                                type="number"
                                                value={range.min_value}
                                                onChange={e => updateReferenceRange(sIndex, cIndex, rIndex, 'min_value', e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                step="any"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-gray-500">Max Value</label>
                                              <input
                                                type="number"
                                                value={range.max_value}
                                                onChange={e => updateReferenceRange(sIndex, cIndex, rIndex, 'max_value', e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                step="any"
                                              />
                                            </div>
                                          </div>
                                          {/* Critical Value fields - only shown for serial_with_chart layout */}
                                          {formData.print_layout_style === 'serial_with_chart' && (
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                              <div>
                                                <label className="text-xs text-gray-500">Critical Value</label>
                                                <label className="text-xs text-gray-400 block">القيمة الحرجة للرسم البياني</label>
                                                <input
                                                  type="number"
                                                  value={range.critical_value || ''}
                                                  onChange={e => updateReferenceRange(sIndex, cIndex, rIndex, 'critical_value', e.target.value)}
                                                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                  step="any"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-xs text-gray-500">Critical Label</label>
                                                <label className="text-xs text-gray-400 block">وصف الخط الحرج</label>
                                                <input
                                                  type="text"
                                                  value={range.critical_label || ''}
                                                  onChange={e => updateReferenceRange(sIndex, cIndex, rIndex, 'critical_label', e.target.value)}
                                                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                  placeholder="مثال: Critical Threshold for H2"
                                                />
                                              </div>
                                            </div>
                                          )}
                                          <div className="flex justify-end">
                                            <button
                                              type="button"
                                              onClick={() => removeReferenceRange(sIndex, cIndex, rIndex)}
                                              className="px-4 py-1.5 bg-red-500 text-white rounded-full text-sm hover:bg-red-600"
                                            >
                                              Remove Range
                                            </button>
                                          </div>
                                        </div>
                                      ))}

                                      <button
                                        type="button"
                                        onClick={() => addReferenceRange(sIndex, cIndex)}
                                        className="w-full px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-100"
                                      >
                                        Add Range
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addComponent(sIndex)}
                              className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded text-sm hover:bg-gray-100"
                            >
                              Add Component
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                </div>

                <div className="px-6 py-4 border-t border-gray-100 bg-slate-50/50 flex justify-end gap-3 sticky bottom-0">
                  <button
                    onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                    className="px-6 py-2.5 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium hover:bg-slate-50 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                  >
                    {saving ? (
                      <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                    ) : 'Save Lab Test'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Details Modal */}
        <AnimatePresence>
          {showDetailsModal && selectedTest && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDetailsModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-100">تفاصيل التحليل</h3>
                  <button onClick={() => setShowDetailsModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div><label className="text-sm text-gray-500">الاسم</label><p className="font-medium">{selectedTest.name_en}</p></div>
                  <div><label className="text-sm text-gray-500">نوع العينة</label><p>{selectedTest.specimen_type || '-'}</p></div>
                  <div><label className="text-sm text-gray-500">القسم</label><p>{selectedTest.department || '-'}</p></div>
                  <div><label className="text-sm text-gray-500">وقت التسليم</label><p>{selectedTest.tat_value} {selectedTest.tat_unit}</p></div>
                  {selectedTest.description && <div><label className="text-sm text-gray-500">الوصف</label><p className="text-sm">{selectedTest.description}</p></div>}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Modal */}
        <AnimatePresence>
          {showDeleteModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">حذف التحليل</h3>
                  <p className="text-gray-500 mb-6">
                    هل أنت متأكد من حذف "{selectedTest?.name_en}"؟
                  </p>
                  <div className="flex gap-3">
                    <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">إلغاء</button>
                    <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                      {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحذف...</> : 'حذف'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Import from Templates Modal */}
        {showImportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-[#1E293B] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  استيراد تحليل من القوالب
                </h2>
                <button onClick={() => { setShowImportModal(false); setSelectedTemplate(null); setTemplateTests([]); setImportedTests(new Set()); }}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {/* Step 1: Select template */}
                {!selectedTemplate ? (
                  <div>
                    <p className="text-sm text-gray-500 mb-4">اختر نظام الوحدات المناسب لمختبرك:</p>
                    <div className="grid gap-3">
                      {templates.map(t => (
                        <button key={t._id} onClick={() => { setSelectedTemplate(t); loadTemplateTests(t._id); }}
                          className="border rounded-xl p-4 text-right hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-between">
                          <div>
                            <div className="font-bold text-gray-800">{t.name_ar || t.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{t.stats?.total_tests} تحليل &bull; {t.stats?.total_categories} تصنيف</div>
                          </div>
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">{t.unit_system === 'conventional' ? 'وحدات تقليدية' : 'وحدات دولية SI'}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Back button */}
                    <button onClick={() => { setSelectedTemplate(null); setTemplateTests([]); setTemplateSearch(''); setTemplateCategoryFilter(''); }}
                      className="text-sm text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-1">
                      &larr; تغيير القالب
                    </button>

                    {/* Search and filter */}
                    <div className="flex gap-3 mb-4">
                      <input
                        type="text"
                        value={templateSearch}
                        onChange={e => { setTemplateSearch(e.target.value); loadTemplateTests(selectedTemplate._id, e.target.value, templateCategoryFilter); }}
                        placeholder="ابحث عن تحليل..."
                        className="flex-1 border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                      />
                      <select
                        value={templateCategoryFilter}
                        onChange={e => { setTemplateCategoryFilter(e.target.value); loadTemplateTests(selectedTemplate._id, templateSearch, e.target.value); }}
                        className="border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="">كل التصنيفات</option>
                        {templateCategories.map((c, i) => (
                          <option key={i} value={c.name_en}>{c.name_ar || c.name_en}</option>
                        ))}
                      </select>
                    </div>

                    {/* Tests list */}
                    {templateLoading ? (
                      <div className="text-center py-10 text-gray-400">جاري التحميل...</div>
                    ) : templateTests.length === 0 ? (
                      <div className="text-center py-10 text-gray-400">لا توجد نتائج</div>
                    ) : (
                      <div className="space-y-2">
                        {templateTests.map((test, i) => (
                          <div key={i} className="border rounded-xl p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-800 text-sm">{test.name_ar || test.name_en}</div>
                              <div className="text-xs text-gray-400 mt-0.5">{test.name_en}</div>
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{test.category_ar || test.category_en}</span>
                                <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{test.specimen_type}</span>
                                <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{test.components_count} عنصر</span>
                                {test.is_serial_test && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">تسلسلي</span>}
                              </div>
                            </div>
                            <button
                              onClick={() => handleImportTest(test)}
                              disabled={importLoading || importedTests.has(test.name_en)}
                              className={`text-xs font-bold px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                                importedTests.has(test.name_en)
                                  ? 'bg-green-100 text-green-700 cursor-default'
                                  : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                              }`}
                            >
                              {importedTests.has(test.name_en) ? 'تم الاستيراد ✓' : importLoading === test.name_en ? 'جاري الاستيراد...' : 'استيراد'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllTests;

